import React, { useState } from 'react';
import { Building2, Home, Wrench, CreditCard, CheckCircle2, Clock, Users, Plus } from 'lucide-react';
import { useAppContext, MaintenanceRequest } from '../contexts/AppContext';
import { formatCurrency, formatDate } from '../utils/formatters';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Modal } from '../components/ui/Modal';
import { EmptyState } from '../components/ui/EmptyState';
import { toast } from 'sonner';
export const FacilityManagement: React.FC = () => {
  const {
    state,
    dispatch,
    processTransaction
  } = useAppContext();
  const [viewRole, setViewRole] = useState<'Tenant' | 'Landlord'>('Tenant');
  // Modals
  const [isMaintenanceOpen, setIsMaintenanceOpen] = useState(false);
  const [isPayRentOpen, setIsPayRentOpen] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);
  // Form states
  const [issueType, setIssueType] = useState('Plumbing');
  const [description, setDescription] = useState('');
  const [urgency, setUrgency] = useState('Medium');
  // Filter data based on role
  const myProperties = state.properties.filter(p => p.role === viewRole);
  const myRequests = state.maintenanceRequests.filter(r => myProperties.some(p => p.id === r.propertyId));
  const handlePayRent = () => {
    const property = myProperties.find(p => p.id === selectedPropertyId);
    if (!property) return;
    if (property.monthlyRent > (state.user?.walletBalance || 0)) {
      toast.error('Insufficient wallet balance to pay rent');
      return;
    }
    processTransaction(-property.monthlyRent, 'Facility', `Rent Payment - ${property.address}`);
    toast.success('Rent Paid Successfully!', {
      description: `${formatCurrency(property.monthlyRent)} paid for ${property.address}`,
      icon: <CheckCircle2 className="text-brand-primary" data-id="element-975" />
    });
    setIsPayRentOpen(false);
  };
  const handleSubmitRequest = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPropertyId || !description) return;
    const newRequest: MaintenanceRequest = {
      id: `req_${Date.now()}`,
      propertyId: selectedPropertyId,
      issueType,
      description,
      urgency,
      status: 'Pending',
      createdAt: new Date().toISOString()
    };
    dispatch({
      type: 'ADD_MAINTENANCE_REQUEST',
      payload: newRequest
    });
    toast.success('Maintenance request submitted successfully');
    setIsMaintenanceOpen(false);
    setDescription('');
    setIssueType('Plumbing');
  };
  const updateRequestStatus = (id: string, status: string) => {
    dispatch({
      type: 'UPDATE_MAINTENANCE_STATUS',
      payload: {
        id,
        status
      }
    });
    toast.success(`Request marked as ${status}`);
  };
  return <div className="space-y-6" data-id="element-976">
      {/* Header & Role Toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-977">
        <div data-id="element-978">
          <h1 className="text-2xl font-bold text-brand-text-dark" data-id="element-979">
            Facility Management
          </h1>
          <p className="text-brand-text-light text-sm" data-id="element-980">
            Manage properties, pay rent, and track maintenance.
          </p>
        </div>

        <div className="bg-gray-100 p-1 rounded-xl inline-flex shrink-0" data-id="element-981">
          <button onClick={() => setViewRole('Tenant')} className={`px-6 py-2 rounded-lg text-sm font-semibold transition-colors ${viewRole === 'Tenant' ? 'bg-white text-brand-text-dark shadow-sm' : 'text-gray-500 hover:text-brand-text-dark'}`} data-id="element-982">
            Tenant View
          </button>
          <button onClick={() => setViewRole('Landlord')} className={`px-6 py-2 rounded-lg text-sm font-semibold transition-colors ${viewRole === 'Landlord' ? 'bg-white text-brand-text-dark shadow-sm' : 'text-gray-500 hover:text-brand-text-dark'}`} data-id="element-983">
            Landlord View
          </button>
        </div>
      </div>

      {viewRole === 'Tenant' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-984">
          {/* Properties List */}
          <div className="lg:col-span-2 space-y-6" data-id="element-985">
            <h3 className="text-lg font-semibold text-brand-text-dark" data-id="element-986">
              My Properties
            </h3>

            {myProperties.length > 0 ? myProperties.map(property => <div key={property.id} className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 flex flex-col sm:flex-row gap-6" data-id="element-987">
                  <div className="w-16 h-16 bg-brand-primary/10 rounded-2xl flex items-center justify-center text-brand-primary shrink-0" data-id="element-988">
                    <Home size={32} data-id="element-989" />
                  </div>

                  <div className="flex-1" data-id="element-990">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-4" data-id="element-991">
                      <div data-id="element-992">
                        <h4 className="font-semibold text-lg text-brand-text-dark" data-id="element-993">
                          {property.address}
                        </h4>
                        <p className="text-sm text-brand-text-light" data-id="element-994">
                          {property.type} • Landlord: {property.landlordName}
                        </p>
                      </div>
                      <div className="text-left sm:text-right" data-id="element-995">
                        <p className="text-sm text-brand-text-light mb-1" data-id="element-996">
                          Monthly Rent
                        </p>
                        <p className="text-xl font-bold text-brand-text-dark" data-id="element-997">
                          {formatCurrency(property.monthlyRent)}
                        </p>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-998">
                      <div className="flex items-center gap-2 text-sm" data-id="element-999">
                        <Clock size={16} className="text-brand-accent" data-id="element-1000" />
                        <span className="text-gray-600" data-id="element-1001">
                          Next due:{' '}
                          <span className="font-semibold text-brand-text-dark" data-id="element-1002">
                            {formatDate(property.nextDueDate)}
                          </span>
                        </span>
                      </div>
                      <div className="flex gap-3 w-full sm:w-auto" data-id="element-1003">
                        <button onClick={() => {
                  setSelectedPropertyId(property.id);
                  setIsMaintenanceOpen(true);
                }} className="flex-1 sm:flex-none px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2" data-id="element-1004">
                          <Wrench size={16} data-id="element-1005" /> Request Repair
                        </button>
                        <button onClick={() => {
                  setSelectedPropertyId(property.id);
                  setIsPayRentOpen(true);
                }} className="flex-1 sm:flex-none px-4 py-2 bg-brand-primary hover:bg-brand-primary-dark text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 shadow-sm" data-id="element-1006">
                          <CreditCard size={16} data-id="element-1007" /> Pay Rent
                        </button>
                      </div>
                    </div>
                  </div>
                </div>) : <EmptyState icon={Home} title="No active leases" description="You are not currently listed as a tenant in any property." data-id="element-1008" />}
          </div>

          {/* Maintenance Requests */}
          <div className="lg:col-span-1" data-id="element-1009">
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 h-full" data-id="element-1010">
              <h3 className="text-lg font-semibold text-brand-text-dark mb-6" data-id="element-1011">
                Maintenance Requests
              </h3>

              <div className="space-y-4" data-id="element-1012">
                {myRequests.length > 0 ? myRequests.map(req => <div key={req.id} className="p-4 rounded-2xl border border-gray-100 hover:border-brand-primary/30 transition-colors" data-id="element-1013">
                      <div className="flex justify-between items-start mb-2" data-id="element-1014">
                        <span className="font-semibold text-sm text-brand-text-dark" data-id="element-1015">
                          {req.issueType}
                        </span>
                        <StatusBadge status={req.status} data-id="element-1016" />
                      </div>
                      <p className="text-sm text-brand-text-light line-clamp-2 mb-3" data-id="element-1017">
                        {req.description}
                      </p>
                      <div className="flex justify-between items-center text-xs" data-id="element-1018">
                        <span className="text-gray-500" data-id="element-1019">
                          {formatDate(req.createdAt)}
                        </span>
                        <span className={`font-medium ${req.urgency === 'High' ? 'text-brand-error' : req.urgency === 'Medium' ? 'text-brand-warning' : 'text-brand-primary'}`} data-id="element-1020">
                          {req.urgency} Priority
                        </span>
                      </div>
                    </div>) : <div className="text-center py-8" data-id="element-1021">
                    <Wrench className="mx-auto text-gray-300 mb-2" size={32} data-id="element-1022" />
                    <p className="text-sm text-gray-500" data-id="element-1023">
                      No active maintenance requests.
                    </p>
                  </div>}
              </div>
            </div>
          </div>
        </div>}

      {viewRole === 'Landlord' && <div className="space-y-6" data-id="element-1024">
          {/* Landlord Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1025">
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-id="element-1026">
              <div className="flex items-center gap-4 mb-2" data-id="element-1027">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600" data-id="element-1028">
                  <Building2 size={20} data-id="element-1029" />
                </div>
                <p className="text-sm font-medium text-brand-text-light" data-id="element-1030">
                  Total Properties
                </p>
              </div>
              <p className="text-3xl font-bold text-brand-text-dark" data-id="element-1031">
                {myProperties.length}
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-id="element-1032">
              <div className="flex items-center gap-4 mb-2" data-id="element-1033">
                <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center text-green-600" data-id="element-1034">
                  <Users size={20} data-id="element-1035" />
                </div>
                <p className="text-sm font-medium text-brand-text-light" data-id="element-1036">
                  Active Tenants
                </p>
              </div>
              <p className="text-3xl font-bold text-brand-text-dark" data-id="element-1037">
                {myProperties.filter(p => p.tenantName).length}
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-id="element-1038">
              <div className="flex items-center gap-4 mb-2" data-id="element-1039">
                <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center text-brand-accent" data-id="element-1040">
                  <Wrench size={20} data-id="element-1041" />
                </div>
                <p className="text-sm font-medium text-brand-text-light" data-id="element-1042">
                  Pending Requests
                </p>
              </div>
              <p className="text-3xl font-bold text-brand-text-dark" data-id="element-1043">
                {myRequests.filter(r => r.status === 'Pending').length}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1044">
            {/* Properties List */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6" data-id="element-1045">
              <div className="flex justify-between items-center mb-6" data-id="element-1046">
                <h3 className="text-lg font-semibold text-brand-text-dark" data-id="element-1047">
                  Portfolio
                </h3>
                <button className="text-sm font-medium text-brand-primary hover:text-brand-primary-dark flex items-center gap-1" data-id="element-1048">
                  <Plus size={16} data-id="element-1049" /> Add Property
                </button>
              </div>

              <div className="space-y-4" data-id="element-1050">
                {myProperties.length > 0 ? myProperties.map(property => <div key={property.id} className="p-4 rounded-2xl border border-gray-100" data-id="element-1051">
                      <h4 className="font-semibold text-brand-text-dark mb-1" data-id="element-1052">
                        {property.address}
                      </h4>
                      <div className="flex justify-between items-center text-sm" data-id="element-1053">
                        <span className="text-brand-text-light" data-id="element-1054">
                          Tenant:{' '}
                          <span className="font-medium text-brand-text-dark" data-id="element-1055">
                            {property.tenantName || 'Vacant'}
                          </span>
                        </span>
                        <span className="font-bold text-brand-primary" data-id="element-1056">
                          {formatCurrency(property.monthlyRent)}/mo
                        </span>
                      </div>
                    </div>) : <EmptyState icon={Building2} title="No properties" description="Add your first property to start managing." data-id="element-1057" />}
              </div>
            </div>

            {/* Maintenance Management */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6" data-id="element-1058">
              <h3 className="text-lg font-semibold text-brand-text-dark mb-6" data-id="element-1059">
                Tenant Requests
              </h3>

              <div className="space-y-4" data-id="element-1060">
                {myRequests.length > 0 ? myRequests.map(req => {
              const prop = myProperties.find(p => p.id === req.propertyId);
              return <div key={req.id} className="p-4 rounded-2xl border border-gray-100" data-id="element-1061">
                        <div className="flex justify-between items-start mb-2" data-id="element-1062">
                          <div data-id="element-1063">
                            <span className="font-semibold text-sm text-brand-text-dark" data-id="element-1064">
                              {req.issueType}
                            </span>
                            <p className="text-xs text-brand-text-light" data-id="element-1065">
                              {prop?.address}
                            </p>
                          </div>
                          <StatusBadge status={req.status} data-id="element-1066" />
                        </div>
                        <p className="text-sm text-gray-600 mb-4" data-id="element-1067">
                          {req.description}
                        </p>

                        {req.status === 'Pending' && <div className="flex gap-2" data-id="element-1068">
                            <button onClick={() => updateRequestStatus(req.id, 'In Progress')} className="flex-1 py-1.5 bg-brand-primary/10 text-brand-primary-dark rounded-lg text-xs font-semibold hover:bg-brand-primary/20 transition-colors" data-id="element-1069">
                              Approve & Assign
                            </button>
                          </div>}
                        {req.status === 'In Progress' && <div className="flex gap-2" data-id="element-1070">
                            <button onClick={() => updateRequestStatus(req.id, 'Completed')} className="flex-1 py-1.5 bg-green-100 text-green-700 rounded-lg text-xs font-semibold hover:bg-green-200 transition-colors" data-id="element-1071">
                              Mark Completed
                            </button>
                          </div>}
                      </div>;
            }) : <div className="text-center py-8" data-id="element-1072">
                    <CheckCircle2 className="mx-auto text-green-400 mb-2" size={32} data-id="element-1073" />
                    <p className="text-sm text-gray-500" data-id="element-1074">
                      All caught up! No pending requests.
                    </p>
                  </div>}
              </div>
            </div>
          </div>
        </div>}

      {/* Pay Rent Modal */}
      <Modal isOpen={isPayRentOpen} onClose={() => setIsPayRentOpen(false)} title="Pay Rent" data-id="element-1075">
        {selectedPropertyId && <div className="space-y-6" data-id="element-1076">
            <div className="bg-gray-50 rounded-xl p-4 text-center" data-id="element-1077">
              <p className="text-sm text-brand-text-light mb-1" data-id="element-1078">Amount Due</p>
              <p className="text-3xl font-bold text-brand-text-dark" data-id="element-1079">
                {formatCurrency(myProperties.find(p => p.id === selectedPropertyId)?.monthlyRent || 0)}
              </p>
            </div>

            <div className="flex justify-between items-center p-4 border border-gray-200 rounded-xl" data-id="element-1080">
              <div className="flex items-center gap-3" data-id="element-1081">
                <div className="w-10 h-10 bg-brand-primary/10 rounded-full flex items-center justify-center text-brand-primary" data-id="element-1082">
                  <CreditCard size={20} data-id="element-1083" />
                </div>
                <div data-id="element-1084">
                  <p className="font-medium text-sm text-brand-text-dark" data-id="element-1085">
                    Pay from Wallet
                  </p>
                  <p className="text-xs text-brand-text-light" data-id="element-1086">
                    Balance: {formatCurrency(state.user?.walletBalance || 0)}
                  </p>
                </div>
              </div>
              <div className="w-4 h-4 rounded-full border-4 border-brand-primary bg-white" data-id="element-1087"></div>
            </div>

            <button onClick={handlePayRent} className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white py-3.5 rounded-xl font-semibold transition-colors shadow-md" data-id="element-1088">
              Confirm Payment
            </button>
          </div>}
      </Modal>

      {/* Maintenance Request Modal */}
      <Modal isOpen={isMaintenanceOpen} onClose={() => setIsMaintenanceOpen(false)} title="Submit Maintenance Request" data-id="element-1089">
        <form onSubmit={handleSubmitRequest} className="space-y-4" data-id="element-1090">
          <div data-id="element-1091">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1092">
              Issue Type
            </label>
            <select value={issueType} onChange={e => setIssueType(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none bg-white" data-id="element-1093">
              <option data-id="element-1094">Plumbing</option>
              <option data-id="element-1095">Electrical</option>
              <option data-id="element-1096">Appliance</option>
              <option data-id="element-1097">Structural</option>
              <option data-id="element-1098">Pest Control</option>
              <option data-id="element-1099">Other</option>
            </select>
          </div>

          <div data-id="element-1100">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1101">
              Urgency
            </label>
            <select value={urgency} onChange={e => setUrgency(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none bg-white" data-id="element-1102">
              <option data-id="element-1103">Low</option>
              <option data-id="element-1104">Medium</option>
              <option data-id="element-1105">High</option>
              <option data-id="element-1106">Emergency</option>
            </select>
          </div>

          <div data-id="element-1107">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1108">
              Description
            </label>
            <textarea required rows={4} value={description} onChange={e => setDescription(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none resize-none" placeholder="Describe the issue in detail..." data-id="element-1109" />
          </div>

          <button type="submit" className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white py-3.5 rounded-xl font-semibold transition-colors mt-4" data-id="element-1110">
            Submit Request
          </button>
        </form>
      </Modal>
    </div>;
};